import constant from "@/common/constant";
import globalService from "@/services/global.service";
import userService from "@/services/auth/user.service";
import AuthAPI from "@/views/modules/auth/auth.api";

/**
 * AuthModule token service (internal JWT v1).
 *
 * Owns/issues the internal access/refresh tokens + sessionId and is the single
 * source of truth for token storage (DI-1). Replaces auth.sso.service.js.
 *
 * Reuses existing constant.STORAGE_KEY keys:
 *  - ACCESS_TOKEN  -> WFL_ACCESS_TOKEN
 *  - REFRESH_TOKEN -> WFL_REFRESH_TOKEN
 *  - SESSION_ID    -> WFL_SESSION_ID (kept for backend Redis session header)
 */
export default {
	/**
	 * Internal username/password login.
	 * Calls POST /api/authenticate via globalService, stores access/refresh
	 * token + sessionId + userInfo.
	 * @param {{username:string,password:string,rememberMe?:boolean}} credentials
	 * @returns {Promise<{state:boolean,message?:string,data?:object}>}
	 */
	async login({ username, password, rememberMe = false } = {}) {
		const response = await globalService.postData_Async(
			AuthAPI.API_Authenticate(),
			{ username, password, rememberMe },
		);

		if (!response || !response.state) {
			return response || { state: false };
		}

		const data = response.data || {};
		await this.storeToken({
			accessToken: data.accessToken,
			refreshToken: data.refreshToken,
			sessionId: data.sessionId,
		});

		if (data.userInfo) {
			await userService.storeAppAuth(data.userInfo);
		}

		return response;
	},

	getAccessToken() {
		return this.get(constant.STORAGE_KEY.ACCESS_TOKEN) || "";
	},

	getRefreshToken() {
		return this.get(constant.STORAGE_KEY.REFRESH_TOKEN) || "";
	},

	getSessionId() {
		return this.get(constant.STORAGE_KEY.SESSION_ID) || "";
	},

	/**
	 * Persist the internal tokens. Missing values are removed so storage never
	 * holds a stale token.
	 * @param {{accessToken?:string,refreshToken?:string,sessionId?:string}} token
	 */
	storeToken({ accessToken, refreshToken, sessionId } = {}) {
		try {
			this.store(constant.STORAGE_KEY.ACCESS_TOKEN, accessToken);
			this.store(constant.STORAGE_KEY.REFRESH_TOKEN, refreshToken);
			this.store(constant.STORAGE_KEY.SESSION_ID, sessionId);
			return Promise.resolve("saved");
		} catch (error) {
			return Promise.reject(error.message || "Failed to save tokens");
		}
	},

	/**
	 * Remove the internal tokens from storage.
	 */
	clear() {
		this.remove(constant.STORAGE_KEY.ACCESS_TOKEN);
		this.remove(constant.STORAGE_KEY.REFRESH_TOKEN);
		this.remove(constant.STORAGE_KEY.SESSION_ID);
	},

	/**
	 * Refresh the internal tokens via /api/re-login using the stored refresh
	 * token. Stores the new tokens on success.
	 * @returns {Promise<{state:boolean,message?:string,data?:object}>}
	 */
	async reLogin() {
		const refreshToken = this.getRefreshToken();
		if (!refreshToken) {
			return Promise.reject(new Error("Missing refresh token"));
		}

		const response = await globalService.postData_Async(
			AuthAPI.API_ReLogin(refreshToken),
			{},
		);

		if (response && response.state && response.data) {
			const data = response.data;
			await this.storeToken({
				accessToken: data.accessToken,
				refreshToken: data.refreshToken,
				sessionId: data.sessionId,
			});
		}

		return response;
	},

	/**
	 * Internal logout: call /api/logout with the stored refresh token, then
	 * clear local token storage regardless of the server result.
	 * @returns {Promise<{state:boolean,message?:string,data?:object}|null>}
	 */
	async logout() {
		const refreshToken = this.getRefreshToken();
		let response = null;
		try {
			if (refreshToken) {
				response = await globalService.postData_Async(
					AuthAPI.API_Logout(refreshToken),
					{},
				);
			}
		} finally {
			this.clear();
		}
		return response;
	},

	store(key, value) {
		value == null || value === undefined
			? localStorage.removeItem(key)
			: localStorage.setItem(key, value);
	},

	get(key) {
		return localStorage.getItem(key);
	},

	remove(key) {
		return localStorage.removeItem(key);
	},
};
